import { useState, useEffect } from 'react';
import { getHealthCheckUrl } from '../services/api';

interface BackendStatus {
    isOnline: boolean;
    lastChecked: Date | null;
    mode: 'local' | 'offline' | 'unknown';
}

const BASE_INTERVAL_MS = 5000;
const MAX_INTERVAL_MS = 30000; // Cap backoff at 30 seconds
const TIMEOUT_MS = 2000;

export function useBackendStatus() {
    const [status, setStatus] = useState<BackendStatus>({
        isOnline: true, 
        lastChecked: null,
        mode: 'unknown'
    });

    useEffect(() => {
        let isMounted = true;
        let timeoutId: NodeJS.Timeout;
        let currentInterval = BASE_INTERVAL_MS;

        const checkHealth = async () => {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
                const healthUrl = getHealthCheckUrl();

                const response = await fetch(healthUrl, {
                    signal: controller.signal,
                    headers: { 'Accept': 'application/json' }
                });

                clearTimeout(timeout);

                if (response.ok) {
                    const data = await response.json();
                    currentInterval = BASE_INTERVAL_MS; // Reset interval on success
                    if (isMounted) {
                        setStatus({
                            isOnline: true,
                            lastChecked: new Date(),
                            mode: data.mode || 'local'
                        });
                    }
                } else {
                    throw new Error('Health check failed');
                }
            } catch (error) {
                // Backoff logic: double the interval up to MAX_INTERVAL_MS
                currentInterval = Math.min(currentInterval * 1.5, MAX_INTERVAL_MS);
                
                if (isMounted) {
                    setStatus(prev => ({
                        isOnline: false,
                        lastChecked: new Date(),
                        mode: 'offline'
                    }));
                }
            } finally {
                if (isMounted) {
                    timeoutId = setTimeout(checkHealth, currentInterval);
                }
            }
        };

        checkHealth();

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, []);

    return status;
}
